var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { I18nAPI, MatchAPI, TrackingAPI, RequestParams, Types, Utils } from '../';
import useHistory from '../hooks/useHistory';
export default function useInit(config, buildDeepLink) {
    const [requestParams, setRequestParams] = React.useState();
    const [i18nStatus, setI18nStatus] = React.useState();
    const [matches, setMatches] = React.useState(undefined);
    const [campaigns, setCampaigns] = React.useState(undefined);
    const [slides, setSlides] = React.useState(undefined);
    const [visible, setVisible] = React.useState(false);
    const apiUpdateIntervalRef = React.useRef(undefined);
    const hasTrackedLoadRef = React.useRef(false);
    const license = config.getLicense(config);
    const { setHistoryListener } = useHistory();
    React.useEffect(() => {
        const unsubscribe = Utils.TCF.subscribe('BannerToken', ({ allowed }) => {
            setRequestParams(RequestParams.init(window.location.href, config, { personalized: allowed }));
        });
        return () => {
            unsubscribe();
        };
    }, [config, window.location.href]);
    React.useEffect(() => {
        const unsubscribe = Utils.TCF.subscribe('BannerToken', ({ allowed }) => {
            setHistoryListener((url) => setRequestParams(RequestParams.init(url, config, { personalized: allowed })));
        });
        return () => {
            unsubscribe();
        };
    }, [config]);
    React.useEffect(() => {
        if (config.tcf) {
            Utils.TCF.init(config.tcf);
        }
    }, [config.tcf]);
    React.useEffect(() => {
        function setupIntersectionObserver() {
            return __awaiter(this, void 0, void 0, function* () {
                const elementToObserve = document.getElementById('root');
                if (!elementToObserve) {
                    console.warn('Root element was not found! Please check the configuration!');
                    return;
                }
                if (typeof window.IntersectionObserver === 'undefined') {
                    // @ts-ignore
                    yield import('intersection-observer');
                }
                const intersectionObserver = new IntersectionObserver(entry => {
                    setVisible(entry[0].isIntersecting);
                }, {
                    rootMargin: '0px 0px 1000px 0px',
                    threshold: 0
                });
                intersectionObserver.observe(elementToObserve);
            });
        }
        if (config.lazyLoading) {
            setupIntersectionObserver();
        }
        else {
            setVisible(true);
        }
    }, [config]);
    React.useEffect(() => {
        if (requestParams) {
            I18nAPI.init(requestParams.language, config.loadI18n)
                .then(() => setI18nStatus('loaded'))
                .catch(() => setI18nStatus('failed'));
        }
    }, [config, requestParams]);
    React.useEffect(() => {
        if (!requestParams ||
            !config.trackingEnabled ||
            !requestParams.size ||
            requestParams.configurator ||
            !requestParams.affiliateId) {
            return;
        }
        const requestParamsAsString = new URLSearchParams(requestParams).toString();
        const affiliateId = requestParams.affiliateId;
        const unsubscribeAffiliateCookieConsent = Utils.TCF.subscribe('BannerAffiliateId', ({ allowed }) => {
            var _a;
            if (allowed) {
                (_a = config.setAffiliateCookie) === null || _a === void 0 ? void 0 : _a.call(config, affiliateId);
            }
        });
        const unsubscribeTrackingConsent = Utils.TCF.subscribe('REST_API_Tracking', ({ allowed }) => {
            if (!hasTrackedLoadRef.current && allowed) {
                hasTrackedLoadRef.current = true;
                TrackingAPI.trackLoad(config.apiHost, affiliateId, requestParamsAsString);
            }
        });
        return () => {
            unsubscribeAffiliateCookieConsent();
            unsubscribeTrackingConsent();
        };
    }, [config, requestParams]);
    function loadSlides(slideDescriptions, matches, campaigns, requestParams, config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (requestParams.size === undefined) {
                setSlides([]);
                return;
            }
            let matchIndex = 0;
            let campaignIndex = 0;
            let slides = (yield Promise.all(slideDescriptions.map((slideDescription) => __awaiter(this, void 0, void 0, function* () {
                switch (slideDescription.type) {
                    case Types.SlideType.Match:
                        // Only add match slides if there a matches available
                        // Because there are multiple layouts depending on the size and the market
                        // the matches can't be assigned to a specific slide.
                        // Examples:
                        // - 300x250 MatchWin: 1 slideDescription + 1 match = 1 slide
                        // - 300x250 CorrectScore: 1 slideDescription + 1 match = n slides
                        // - 728x90 MatchWin: 1 slideDescription + 2 matches = 1 slide
                        if (matches.length === 0)
                            return null;
                        return { type: Types.SlideType.Match, index: matchIndex++ };
                    case Types.SlideType.Creative: {
                        const creative = yield config.loadCreative(slideDescription.id, requestParams, config);
                        if (!creative)
                            return null;
                        return {
                            type: Types.SlideType.Creative,
                            creativeId: slideDescription.id,
                            url: creative,
                            campaignLink: undefined
                        };
                    }
                    case Types.SlideType.Campaign: {
                        let campaign = campaigns[campaignIndex++];
                        if (!campaign)
                            return null;
                        const creative = yield config.loadCreative(campaign.creativeId, requestParams, config);
                        if (!creative)
                            return null;
                        return {
                            type: Types.SlideType.Campaign,
                            creativeId: campaign.creativeId,
                            url: creative,
                            campaignId: campaign.id,
                            campaignLink: campaign.link
                        };
                    }
                    default:
                        return null;
                }
            })))).filter((slide) => slide !== null);
            if (slides.length === 0 && config.fallbackCreativeId) {
                console.log(`Empty slides -> Fallback to creative ${config.fallbackCreativeId}`);
                const creative = yield config.loadCreative(config.fallbackCreativeId, requestParams, config);
                if (creative) {
                    slides = [
                        {
                            type: Types.SlideType.Creative,
                            creativeId: config.fallbackCreativeId,
                            url: creative,
                            campaignLink: undefined
                        }
                    ];
                }
            }
            console.log('slideDescriptions', slideDescriptions);
            console.log('slides', slides);
            setSlides(slides);
        });
    }
    const slideDescriptions = requestParams ? requestParams.slide : null;
    React.useEffect(() => {
        if (requestParams && slideDescriptions && matches && campaigns) {
            loadSlides(slideDescriptions, matches, campaigns, requestParams, config);
        }
    }, [slideDescriptions, matches, campaigns, requestParams, config]);
    const getCampaigns = React.useCallback(() => {
        function getCampaigns(params) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!params.template || !params.size) {
                    setCampaigns([]);
                    return;
                }
                const campaignCount = params.slide.filter(slideDescription => slideDescription.type === Types.SlideType.Campaign).length;
                const campaigns = yield config.loadCampaigns(campaignCount, params, config);
                setCampaigns(campaigns);
            });
        }
        if (requestParams) {
            getCampaigns(requestParams);
        }
    }, [requestParams]);
    const getMatches = React.useCallback(() => {
        function getMatches(params, _license) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!params.template || !params.size) {
                    setMatches([]);
                    return;
                }
                const matchSlideCount = params.slide.filter(slideDescription => slideDescription.type === Types.SlideType.Match).length;
                const maxMatchCountPerSlide = config.getMaxMatchCountPerSlide(params.template, params.size);
                const targetCount = matchSlideCount * maxMatchCountPerSlide;
                let collectedMatches = [];
                const normalMatchParams = {
                    language: params.language,
                    country: params.country,
                    sport: params.sport,
                    region: params.region,
                    league: params.league,
                    team: params.team,
                    autoFill: params.autoFill,
                    supportedSports: config.supportedSports ? config.supportedSports.join(',') : undefined,
                    market: params.market,
                    contestId: params.contestId,
                    url: RequestParams.getContextSensitiveURL(params),
                    sortBy: params.sortBy,
                    beginBefore: params.beginBefore,
                    license: _license
                };
                const customerBetslipSelection = params.customerBetslipSelection;
                if (customerBetslipSelection && customerBetslipSelection.length > 0) {
                    const [betslipError, betslipMatches] = yield MatchAPI.getMatches(config.apiHost, {
                        language: params.language,
                        market: params.market,
                        country: params.country,
                        license: _license,
                        beginBefore: params.beginBefore,
                        contestId: customerBetslipSelection
                    });
                    if (betslipError) {
                        setMatches([]);
                        return;
                    }
                    if (betslipMatches && typeof betslipMatches !== 'string') {
                        collectedMatches = betslipMatches;
                    }
                }
                if (collectedMatches.length < targetCount) {
                    const remainingCount = targetCount - collectedMatches.length;
                    const [error, matches] = yield MatchAPI.getMatches(config.apiHost, Object.assign(Object.assign({}, normalMatchParams), { matchCount: String(remainingCount) }));
                    if (error) {
                        setMatches([]);
                        return;
                    }
                    if (matches && typeof matches !== 'string') {
                        const existingIds = new Set(collectedMatches.map(match => match.id));
                        const additionalMatches = matches.filter(match => !existingIds.has(match.id));
                        collectedMatches = collectedMatches.concat(additionalMatches);
                    }
                }
                setMatches(Utils.MatchFormatter.getFilteredMatches(collectedMatches.slice(0, targetCount), params));
            });
        }
        if (requestParams) {
            getMatches(requestParams, license);
        }
    }, [requestParams, license]);
    let updateApiInterval = visible ? Number(requestParams === null || requestParams === void 0 ? void 0 : requestParams.updateApiInterval) : undefined;
    React.useEffect(() => {
        if (updateApiInterval) {
            console.log('Starting updates!');
            apiUpdateIntervalRef.current = window.setInterval(() => {
                getMatches();
            }, updateApiInterval);
        }
        else {
            if (apiUpdateIntervalRef.current) {
                console.log('Stopping updates!');
                clearInterval(apiUpdateIntervalRef.current);
            }
        }
    }, [updateApiInterval]);
    const matchesLoaded = matches !== undefined;
    const initMocks = React.useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const setupWorker = (yield import('../api/Mocks')).default;
        const worker = setupWorker(config.apiHost);
        worker.start();
    }), []);
    React.useEffect(() => {
        if (!matchesLoaded && visible) {
            if (requestParams) {
                if (requestParams.mocks === 'true') {
                    initMocks().finally(() => {
                        getMatches();
                    });
                }
                else {
                    getMatches();
                }
            }
        }
    }, [config, requestParams, matchesLoaded, visible]);
    React.useEffect(() => {
        getCampaigns();
    }, [requestParams]);
    let loading = i18nStatus === undefined || matches === undefined || slides === undefined;
    let templateParams = undefined;
    let configuratorParams = undefined;
    if (!loading && requestParams && slides && matches && campaigns) {
        if (requestParams.template && requestParams.size) {
            templateParams = {
                // Force fallback if i18n cannot be loaded
                matches: i18nStatus === 'failed' ? [] : matches,
                campaigns,
                slides,
                requestParams,
                settings: {
                    language: requestParams.language,
                    defaultLanguage: config.defaults.language,
                    trendsFallbackLanguages: config.trendsFallbackLanguages,
                    oddsFormat: requestParams.oddsFormat,
                    currency: requestParams.currency,
                    market: requestParams.market,
                    theme: requestParams.theme,
                    stake: requestParams.stake,
                    loadImage: config.loadImage,
                    resolvePath: config.resolvePath,
                    apiHost: config.apiHost,
                    license: license
                },
                buildDeepLink: params => buildDeepLink(Object.assign(Object.assign({}, params), { requestParams, stake: requestParams.stake, encodeDeepLink: (deepLink) => {
                        const encodedDeeplinkUrl = new URL(deepLink);
                        if (requestParams.passThrough) {
                            requestParams.passThrough.forEach((value, key) => {
                                encodedDeeplinkUrl.searchParams.append(key, value);
                            });
                        }
                        return `${requestParams.adServerClickPrefix}${encodedDeeplinkUrl.toString()}`;
                    } }))
            };
        }
        else {
            configuratorParams = {
                config,
                requestParams
            };
        }
    }
    return {
        loading,
        templateParams,
        requestParams,
        configuratorParams,
        visible,
        matches
    };
}
//# sourceMappingURL=useInit.js.map